import { Accordion, Button, ExternalLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { IconCard } from '../../../components/icon-card';
import { isAfterBFSReviewSubmission } from '../../../constants/shopify';
import { useEnv } from '../../../context/env-context';
import { useHandlers, useSite } from '../../../context/page-context';
import { useModal } from '../../../hooks/use-modal';
import type { I18nPrefix } from '../../../i18n/prefix';
import { cn } from '../../../lib/styles';
import { type CatalogFeature, EXAMPLE_ICONS } from '../constants/features-catalog';
import { AdvancedFeatureModal } from './advanced-feature-modal';
import { FeatureIconTile } from './feature-icon-tile';

const BASE = 'features:explore.pages.features_page' satisfies I18nPrefix;

export type FeatureAccordionItem = CatalogFeature & { locked: boolean };

/**
 * Per category accordion of features - each feature shows a primary action and sometimes a
 * "Read the docs" link
 */
export function FeatureAccordion({ features }: { features: readonly FeatureAccordionItem[] }) {
  const { navigationHandler } = useHandlers();
  const { platform } = useSite();
  const { created_at } = useEnv();
  const [openId, setOpenId] = useState<string | null>(null);

  const { isOpen, openModal, closeModal } = useModal();

  const [advancedFeature, setAdvancedFeature] = useState<
    { name: string; path: string } | undefined
  >(undefined);

  function handlePrimary({ primaryLink, id, category }: CatalogFeature) {
    if ('id' in primaryLink) {
      navigationHandler.navigateById(primaryLink.id);
      return;
    }

    // TODO: discuss to remove or keep after we get BFS badge
    if (
      isAfterBFSReviewSubmission(platform, created_at) &&
      category !== 'basics' &&
      id !== 'b2b-catalogs'
    ) {
      setAdvancedFeature({ name: t(`${BASE}.features.${id}.title`), path: primaryLink.path });
      openModal();
      return;
    }

    navigationHandler.navigate(
      primaryLink.path,
      primaryLink.external
        ? { absolute: true, newTab: true }
        : { openInDashboard: primaryLink.openInDashboard },
    );
  }

  return (
    <>
      <Accordion>
        {features.map((feature) => {
          const { id, icon, docsUrl, minPlan, locked, image, examples } = feature;

          const body = <Text variant="secondary">{t(`${BASE}.features.${id}.body`)}</Text>;

          const actions = (
            <div className="flex flex-wrap items-center gap-4">
              <Button variant="primary" onClick={() => handlePrimary(feature)}>
                {t(`${BASE}.features.${id}.action`)}
              </Button>
              {docsUrl && (
                <ExternalLink link={docsUrl}>{t(`${BASE}.cta.read_the_docs`)}</ExternalLink>
              )}
            </div>
          );

          return (
            <Accordion.Item
              key={id}
              icon={<FeatureIconTile icon={icon} />}
              title={t(`${BASE}.features.${id}.title`)}
              subtitle={t(`${BASE}.features.${id}.description`)}
              badge={
                locked && minPlan
                  ? {
                      text: t(`${BASE}.plan_badge`, { plan: t(`labels:plans.${minPlan}`) }),
                      variant: 'orange',
                      icon: 'lock',
                    }
                  : undefined
              }
              isExpanded={openId === id}
              onExpandedChange={(expanded) => setOpenId(expanded ? id : null)}
            >
              {examples ? (
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {examples.map((exampleId) => (
                      <IconCard
                        key={exampleId}
                        icon={EXAMPLE_ICONS[exampleId]}
                        title={t(`${BASE}.examples.${exampleId}.title`)}
                        subtitle={t(`${BASE}.examples.${exampleId}.subtitle`)}
                      />
                    ))}
                  </div>
                  {body}
                  {actions}
                </div>
              ) : (
                <div
                  className={cn(
                    'flex flex-col gap-4',
                    image && 'md:flex-row md:items-center md:justify-between md:gap-8',
                  )}
                >
                  <div className="flex flex-col gap-3 md:flex-1">
                    {body}
                    {actions}
                  </div>
                  {image && (
                    <img
                      src={image}
                      alt={t(`${BASE}.features.${id}.title`)}
                      className="mx-auto max-h-32 w-auto rounded-lg md:mx-0 md:shrink-0"
                    />
                  )}
                </div>
              )}
            </Accordion.Item>
          );
        })}
      </Accordion>
      <AdvancedFeatureModal
        isOpen={isOpen}
        closeModal={closeModal}
        featureName={advancedFeature?.name ?? ''}
        onConfirm={() =>
          advancedFeature &&
          navigationHandler.navigate(advancedFeature?.path, { openInDashboard: true })
        }
      />
    </>
  );
}
